import { mapErrorsToDotNotation } from './ErrorsService';

export const isCheckboxOrRadio = (type?: string): boolean => {
    if (!type) {
        return false;
    }
    return type === 'checkbox' || type === 'radio';
};

export const getCheckedValue = <FieldValue = any>(
    fieldValue: FieldValue,
    options?: { type?: string },
) => {
    if (!options?.type) {
        return undefined;
    }
    if (!isCheckboxOrRadio(options.type)) {
        return undefined;
    }
    return fieldValue;
};

/**
 * Retrieves inner errors for a given field name from a flattened errors object.
 * @param {string} name - The name of the field to check for inner errors.
 * @param {Record<string, any>} [errors={}] - The errors object to search through.
 * @returns {string | undefined} A string containing all inner errors, or undefined if no inner errors are found.
 * @example
 * ```typescript
 * const errors = {
 *   'user.name': 'Name is required',
 *   'user.email': 'Invalid email',
 *   'root.user.age': 'Must be over 18'
 * };
 * console.log(getInnerError('user', errors));
 * // Output: "Name is required\nInvalid email\nMust be over 18"
 *
 * console.log(getInnerError('address', errors));
 * // Output: undefined
 * ```
 */
export function getInnerError(name: string, errors = {}): string | undefined {
    const flatErrors = mapErrorsToDotNotation<any>(errors);

    const innerErrorsKeys = Object.keys(flatErrors).filter(
        (key) => key.startsWith(`${name}.`) || key.startsWith(`root.${name}.`),
    );

    if (innerErrorsKeys.length === 0) {
        return undefined;
    }

    return innerErrorsKeys.map((key) => flatErrors[key]).join('\n');
}
